/**
 * Garden Filter
 * =============
 * Client-side filtering on the garden index page (/en/ and /fr/).
 * Filters post cards by content type, growth stage, and topics
 * using data attributes on the card elements.
 *
 * Also powers the Crossroads page (/en/crossroads/, /fr/carrefour/),
 * where cards are wrapped in .gh-crossroads-card-wrap with data-type
 * and data-topics attributes, and filters include both type and topic pills.
 *
 * This operates on the DOM — Ghost renders all cards server-side,
 * and JS shows/hides them based on active filters.
 */

export default function gardenFilter() {

    // ── Crossroads page ───────────────────────────────────────────
    const crossroadsGrid = document.getElementById('gh-crossroads-grid');
    if (crossroadsGrid) {
        initCrossroadsFilter(crossroadsGrid);
        return;
    }

    // ── Garden index pages (/en/, /fr/) ───────────────────────────
    const filterContainer = document.querySelector('.gh-garden-filters');
    if (!filterContainer) return;

    const cards = document.querySelectorAll('.gh-garden-content .gh-card');
    const resultsEl = document.querySelector('.gh-garden-results');
    const contentArea = document.querySelector('.gh-garden-content');
    const filterBtns = filterContainer.querySelectorAll('.gh-filter-btn');

    const activeFilters = {
        type: null,
        stage: null,
    };

    filterBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
            const group = btn.dataset.filterGroup;
            const value = btn.dataset.filterValue;

            if (activeFilters[group] === value) {
                activeFilters[group] = null;
                btn.classList.remove('is-active');
            } else {
                filterContainer.querySelectorAll(`.gh-filter-btn[data-filter-group="${group}"]`).forEach((b) => {
                    b.classList.remove('is-active');
                });
                activeFilters[group] = value;
                btn.classList.add('is-active');
            }

            applyFilters();
        });
    });

    function applyFilters() {
        if (contentArea) contentArea.classList.add('is-filtering');

        let visibleCount = 0;

        requestAnimationFrame(() => {
            cards.forEach((card) => {
                const cardType = card.dataset.type || '';
                const cardStage = card.dataset.stage || '';

                const matchesType = !activeFilters.type || cardType === activeFilters.type;
                const matchesStage = !activeFilters.stage || cardStage === activeFilters.stage;

                if (matchesType && matchesStage) {
                    card.style.display = '';
                    visibleCount++;
                } else {
                    card.style.display = 'none';
                }
            });

            if (resultsEl) {
                const isFiltered = activeFilters.type || activeFilters.stage;
                if (isFiltered) {
                    const lang = document.documentElement.lang;
                    resultsEl.textContent = lang === 'fr'
                        ? `${visibleCount} résultat${visibleCount !== 1 ? 's' : ''}`
                        : `${visibleCount} result${visibleCount !== 1 ? 's' : ''}`;
                    resultsEl.style.display = '';
                } else {
                    resultsEl.style.display = 'none';
                }
            }

            const emptyEl = document.querySelector('.gh-garden-empty');
            if (emptyEl) emptyEl.style.display = visibleCount === 0 ? '' : 'none';

            if (contentArea) setTimeout(() => contentArea.classList.remove('is-filtering'), 150);
        });
    }
}

// ── Crossroads filter logic ────────────────────────────────────────────────
function initCrossroadsFilter(grid) {
    const wraps = grid.querySelectorAll('.gh-crossroads-card-wrap');
    const resultsEl = document.querySelector('.gh-crossroads-results');
    const emptyEl   = document.querySelector('.gh-crossroads-empty');
    const lang = document.documentElement.lang === 'fr' ? 'fr' : 'en';

    // Collect all pill buttons (type + topic)
    const typePills  = document.querySelectorAll('.gh-crossroads-type-filters .gh-filter-btn');
    const topicPills = document.querySelectorAll('.gh-crossroads-topic-bar .gh-topic-pill');

    const activeFilters = { type: '', topic: '' };

    function handlePillClick(pills, group) {
        pills.forEach((pill) => {
            pill.addEventListener('click', () => {
                const value = pill.dataset.filterValue;

                // Clicking the already-active pill = show all (reset group)
                if (activeFilters[group] === value && value !== '') {
                    activeFilters[group] = '';
                    pill.classList.remove('is-active');
                    // re-activate the "All" pill
                    pills.forEach((p) => { if (p.dataset.filterValue === '') p.classList.add('is-active'); });
                } else {
                    pills.forEach((p) => p.classList.remove('is-active'));
                    pill.classList.add('is-active');
                    activeFilters[group] = value;
                }

                applyFilters();
            });
        });
    }

    handlePillClick(typePills, 'type');
    handlePillClick(topicPills, 'topic');

    function applyFilters() {
        let visible = 0;

        wraps.forEach((wrap) => {
            const cardType   = wrap.dataset.type   || '';
            const cardTopics = wrap.dataset.topics  || '';

            const matchesType  = !activeFilters.type  || cardType === activeFilters.type;
            // topic slugs are space-separated in data-topics; check substring
            const matchesTopic = !activeFilters.topic || cardTopics.split(' ').includes(activeFilters.topic);

            if (matchesType && matchesTopic) {
                wrap.style.display = '';
                visible++;
            } else {
                wrap.style.display = 'none';
            }
        });

        // Results count
        if (resultsEl) {
            const isFiltered = activeFilters.type || activeFilters.topic;
            if (isFiltered) {
                resultsEl.textContent = lang === 'fr'
                    ? `${visible} résultat${visible !== 1 ? 's' : ''}`
                    : `${visible} result${visible !== 1 ? 's' : ''}`;
                resultsEl.style.display = '';
            } else {
                resultsEl.style.display = 'none';
            }
        }

        if (emptyEl) emptyEl.style.display = visible === 0 ? '' : 'none';
    }
}
